import React, { useEffect, useState, SyntheticEvent, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  InputAdornment,
  Checkbox,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  Collapse,
  IconButton,
  Menu,
  Chip,
  Snackbar,
} from '@mui/material';
import {
  CheckBox,
  CheckBoxOutlineBlank,
  Info as InfoIcon,
  Flag,
  FlagOutlined,
  ExpandMore,
  ExpandLess,
  FilterList,
  Launch as LaunchIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import api from '../api/client';
import {
  Client,
  Product,
  Ranking,
  RankingPosition,
  RankingWithProduct,
  ProcessedRanking,
  AsinChange,
  RankingData,
  OrganizedRankings,
  ProductRanking,
  RankingsViewState,
  RankingsViewHandlers,
  RankingsViewUtils,
  WeeklyPositions,
  KeywordGroups,
  RankingFilter,
  Keyword,
  KeywordTag
} from '../types';
import PeriodPerformanceSummary from './PeriodPerformanceSummary';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { debounce } from 'lodash';
import { getCountryFlag, Market, getMarketLabel, getAmazonDomain, getAmazonProductUrl } from '../utils/marketUtils';
import { SelectChangeEvent } from '@mui/material/Select';
import { useSearchParams } from 'react-router-dom';

const RankingsView: React.FC = () => {
  // State
  const [state, setState] = useState<RankingsViewState>({
    selectedClient: null,
    selectedProducts: [],
    clients: [],
    organizedRankings: [],
    error: '',
    isLoading: false,
    customAsins: ''
  });

  const [filters, setFilters] = useState<RankingFilter>({
    client_id: 0,
    product_ids: '',
    days: 90,
    min_search_volume: 100,
    max_position: 100,
    keywords: [],
    market: 1,
    custom_asins: ''
  });

  // State for keyword tags
  const [keywordTags, setKeywordTags] = useState<KeywordTag[]>([]);

  const [products, setProducts] = useState<Product[]>([]);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [unfilteredRankings, setUnfilteredRankings] = useState<OrganizedRankings[]>([]);

  // Add state to track focus and pending changes
  const [isPending, setIsPending] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const pendingTimeoutRef = useRef<NodeJS.Timeout>();
  const lastChangeRef = useRef<number>(Date.now());

  // Add state to track dropdown open states
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);
  const [keywordsDropdownOpen, setKeywordsDropdownOpen] = useState(false);
  const [tagMenuAnchorEl, setTagMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [inputValue, setInputValue] = useState('');
  
  // Add state to track if advanced filters are expanded
  const [advancedFiltersExpanded, setAdvancedFiltersExpanded] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // URL params and copy feedback
  const [searchParams, setSearchParams] = useSearchParams();
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const hasAppliedUrlRef = useRef(false);

  // Add sorting state
  const [sortConfig, setSortConfig] = useState<{
    key: 'searchVolume' | 'lastPosition' | 'keyword' | 'ctrScore' | 'growth_opportunity' | 'main_niche_score';
    direction: 'asc' | 'desc';
  }>({
    key: 'ctrScore',
    direction: 'desc'
  });

  // Add filter states
  const [filterState, setFilterState] = useState({
    position: { min: '', max: '' },
    ctrScore: { min: '', max: '' },
    growth_opportunity: { min: '', max: '' },
    main_niche_score: { min: '', max: '' },
    searchVolume: { min: '', max: '' }
  });

  // Add input state separate from filter state
  const [inputState, setInputState] = useState({
    position: { min: '', max: '' },
    ctrScore: { min: '', max: '' },
    growth_opportunity: { min: '', max: '' },
    main_niche_score: { min: '', max: '' },
    searchVolume: { min: '', max: '' }
  });

  // Define filter state type
  type FilterStateType = {
    position: { min: string; max: string };
    ctrScore: { min: string; max: string };
    growth_opportunity: { min: string; max: string };
    main_niche_score: { min: string; max: string };
    searchVolume: { min: string; max: string };
  };

  // Function to apply pending changes - only for server-side filters
  const applyPendingChanges = () => {
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
    }

    setIsPending(false);
    // Rely on loadRankings effect to fetch after pending finishes
  };

  // Fetch rankings is handled by the loadRankings effect below

  // Cache the numeric values for each ranking
  const rankingsWithNumericValues = useMemo(() => {
    return unfilteredRankings.map(ranking => {
      const firstProduct = ranking.rankings[0]?.products[0];
      if (!firstProduct) return null;
      
      // Find the maximum main_niche_score across all rankings and products
      const allNicheScores = ranking.rankings
        .flatMap(r => (r?.products ? r.products.map(p => p?.main_niche_score || 0) : []))
        .filter((item): item is NonNullable<typeof item> => item !== null);
      const maxNicheScore = allNicheScores.length > 0 ? Math.max(...allNicheScores) : 0;

      return {
        ranking,
        position: firstProduct.position,
        ctrScore: firstProduct.ctr_score || 0,
        growth_opportunity: firstProduct.growth_opportunity || 0,
        main_niche_score: maxNicheScore,
        searchVolume: ranking.searchVolume
      };
    }).filter((item): item is NonNullable<typeof item> => item !== null);
  }, [unfilteredRankings]);

  // Memoize filter value parsing to avoid repeated conversions
  const parsedFilterValues = useMemo(() => {
    const parseValue = (value: string) => {
      const num = Number(value);
      return value === '' ? null : isNaN(num) ? null : num;
    };

    return {
      position: {
        min: parseValue(filterState.position.min),
        max: parseValue(filterState.position.max)
      },
      ctrScore: {
        min: parseValue(filterState.ctrScore.min),
        max: parseValue(filterState.ctrScore.max)
      },
      growth_opportunity: {
        min: parseValue(filterState.growth_opportunity.min),
        max: parseValue(filterState.growth_opportunity.max)
      },
      main_niche_score: {
        min: parseValue(filterState.main_niche_score.min),
        max: parseValue(filterState.main_niche_score.max)
      },
      searchVolume: {
        min: parseValue(filterState.searchVolume.min),
        max: parseValue(filterState.searchVolume.max)
      }
    };
  }, [filterState]);

  // Memoize the filtered results with optimized filtering
  const filteredRankings = useMemo(() => {
    // Start with keyword filtering since it's less CPU intensive
    let results = rankingsWithNumericValues;
    
    // Apply keyword filters from the filters state
    if (filters.keywords.length > 0) {
      const keywordSet = new Set(filters.keywords);
      results = results.filter(item => keywordSet.has(item.ranking.keyword));
    }

    // Apply numeric filters from filterState
    const hasActiveFilters = Object.values(parsedFilterValues).some(
      filter => filter.min !== null || filter.max !== null
    );

    if (!hasActiveFilters) {
      return results.map(item => item.ranking);
    }

    // Apply numeric filters
    return results.filter(({ position, ctrScore, growth_opportunity, main_niche_score, searchVolume }) => {
      const { position: posFilter, ctrScore: ctrFilter, growth_opportunity: growthFilter, main_niche_score: nicheFilter, searchVolume: volFilter } = parsedFilterValues;

      // Only check filters that have values
      if (posFilter.min !== null && position < posFilter.min) return false;
      if (posFilter.max !== null && position > posFilter.max) return false;
      if (ctrFilter.min !== null && ctrScore < ctrFilter.min) return false;
      if (ctrFilter.max !== null && ctrScore > ctrFilter.max) return false;
      if (growthFilter.min !== null && growth_opportunity < growthFilter.min) return false;
      if (growthFilter.max !== null && growth_opportunity > growthFilter.max) return false;
      if (nicheFilter.min !== null && main_niche_score < nicheFilter.min) return false;
      if (nicheFilter.max !== null && main_niche_score > nicheFilter.max) return false;
      if (volFilter.min !== null && searchVolume < volFilter.min) return false;
      if (volFilter.max !== null && searchVolume > volFilter.max) return false;

      return true;
    }).map(item => item.ranking);
  }, [rankingsWithNumericValues, filters.keywords, parsedFilterValues]);

  // Debounced function to update filter state with longer delay
  const debouncedUpdateFilters = useCallback(
    debounce((newState: FilterStateType | ((prev: FilterStateType) => FilterStateType)) => {
      setFilterState(newState);
    }, 300),
    []
  );

  // Handle input changes with optimized update logic
  const handleInputChange = (
    filterType: keyof FilterStateType,
    bound: 'min' | 'max',
    value: string
  ) => {
    // Immediately update visual state
    setInputState(prev => ({
      ...prev,
      [filterType]: {
        ...prev[filterType],
        [bound]: value
      }
    }));

    // Schedule filter update
    debouncedUpdateFilters((prev: FilterStateType): FilterStateType => ({
      ...prev,
      [filterType]: {
        ...prev[filterType],
        [bound]: value
      }
    }));
  };

  // Cleanup debounced handler
  useEffect(() => {
    return () => {
      debouncedUpdateFilters.cancel();
    };
  }, [debouncedUpdateFilters]);

  // Separate sorting logic
  const filteredAndSortedRankings = useMemo(() => {
    const results = [...filteredRankings];
    
    results.sort((a, b) => {
      switch (sortConfig.key) {
        case 'searchVolume':
          return sortConfig.direction === 'asc' 
            ? a.searchVolume - b.searchVolume
            : b.searchVolume - a.searchVolume;
        case 'keyword':
          return sortConfig.direction === 'asc'
            ? a.keyword.localeCompare(b.keyword)
            : b.keyword.localeCompare(a.keyword);
        case 'lastPosition': {
          const aLastPos = a.rankings[0]?.products[0]?.position || 999;
          const bLastPos = b.rankings[0]?.products[0]?.position || 999;
          return sortConfig.direction === 'asc'
            ? aLastPos - bLastPos
            : bLastPos - aLastPos;
        }
        case 'ctrScore': {
          const aCtrScore = a.rankings[0]?.products[0]?.ctr_score || 0;
          const bCtrScore = b.rankings[0]?.products[0]?.ctr_score || 0;
          return sortConfig.direction === 'asc'
            ? aCtrScore - bCtrScore
            : bCtrScore - aCtrScore;
        }
        case 'growth_opportunity': {
          const aVisibility = a.rankings[0]?.products[0]?.growth_opportunity || 0;
          const bVisibility = b.rankings[0]?.products[0]?.growth_opportunity || 0;
          return sortConfig.direction === 'asc'
            ? aVisibility - bVisibility
            : bVisibility - aVisibility;
        }
        case 'main_niche_score': {
          // Use the maximum main_niche_score across all positions and products for each keyword
          const aNicheScores = a.rankings
            .flatMap(r => (r?.products ? r.products.map(p => p?.main_niche_score || 0) : []))
            .filter((item): item is NonNullable<typeof item> => item !== null);
          const bNicheScores = b.rankings
            .flatMap(r => (r?.products ? r.products.map(p => p?.main_niche_score || 0) : []))
            .filter((item): item is NonNullable<typeof item> => item !== null);

          const aMax = aNicheScores.length > 0 ? Math.max(...aNicheScores) : 0;
          const bMax = bNicheScores.length > 0 ? Math.max(...bNicheScores) : 0;

          return sortConfig.direction === 'asc'
            ? aMax - bMax
            : bMax - aMax;
        }
        default:
          return 0;
      }
    });

    return results;
  }, [filteredRankings, sortConfig]);

  // Get available keywords from the unfiltered rankings
  const availableKeywords = useMemo(() => {
    return unfilteredRankings.map(ranking => ({
      id: 0,
      keyword: ranking.keyword,
      client_id: state.selectedClient?.id || 0,
      created_at: new Date().toISOString(),
      search_volume: ranking.searchVolume
    } satisfies Keyword));
  }, [unfilteredRankings, state.selectedClient]);

  // Update keywords when available keywords change
  useEffect(() => {
    setKeywords(availableKeywords);
  }, [availableKeywords]);

  // Utility Functions
  const utils: RankingsViewUtils = {
    isValidPosition: (position: RankingPosition): boolean => {
      return position && position.position > 0;
    },

    getWeekStartDate: (date: string): string => {
      // Convert to UTC date to avoid timezone issues
      const d = new Date(date);
      const utc = new Date(
        Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
      );
      // Get to the start of the week (Sunday) in UTC
      utc.setUTCDate(utc.getUTCDate() - utc.getUTCDay());
      return utc.toISOString().split('T')[0];
    },

    getDaysBetween: (date1: string, date2: string): number => {
      const d1 = new Date(date1);
      const d2 = new Date(date2);
      return Math.abs(Math.floor((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)));
    },

    getPositionColor: (position: number): string => {
      if (position <= 3) return '#4caf50';  // Green for top 3
      if (position <= 10) return '#8bc34a'; // Light green for top 10
      if (position <= 25) return '#ffc107'; // Yellow for top 25
      if (position <= 50) return '#ff9800'; // Orange for top 50
      return '#f44336';                     // Red for > 50
    },

    getPositionChange: (current: number, previous: number | undefined): string => {
      if (!previous) return '';
      const diff = previous - current;
      if (diff > 0) return `↑${diff}`;
      if (diff < 0) return `↓${Math.abs(diff)}`;
      return '=';
    },

    processRanking: (ranking: Ranking, products: Product[]): ProcessedRanking => {
      // For custom ASINs, use the productName from the ranking directly
      let productName = ranking.productName || '';
      
      // Only try to find the product if we have products and a product_id
      if (products.length > 0 && ranking.product_id) {
        const product = products.find((p: Product) => p.id === ranking.product_id);
        if (product) {
          productName = product.name;
        }
      }
      
      const positions = [...ranking.positions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const latestPosition = positions.find(p => utils.isValidPosition(p));
      const previousPosition = positions.find((p, idx) => idx > 0 && utils.isValidPosition(p));

      return {
        ...ranking,
        productName,
        currentPosition: latestPosition?.position || 0,
        dateUpdated: latestPosition?.date || '',
        currentAsin: latestPosition?.asin || '',
        positionChange: previousPosition ? (previousPosition.position - (latestPosition?.position || 0)) : 0,
        asinChanges: []
      };
    },

    organizeRankingsByKeyword: (rankings: RankingWithProduct[]): OrganizedRankings[] => {
      // Get all unique dates (weeks)
      const allPositions: { date: string; position: number }[] = [];
      rankings.forEach(ranking => {
        ranking.positions.forEach((pos: RankingPosition) => {
          if (utils.isValidPosition(pos)) {
            allPositions.push({
              date: utils.getWeekStartDate(pos.date),
              position: pos.position
            });
          }
        });
      });

      // Sort dates in descending order and get unique dates
      const sortedDates = Array.from(new Set(allPositions.map(p => p.date)))
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

      // Group rankings by keyword
      const keywordGroups = rankings.reduce<KeywordGroups>((groups, ranking) => {
        const key = ranking.keyword;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(ranking);
        return groups;
      }, {});

      // Transform into organized format
      return Object.entries(keywordGroups).map(([keyword, rankings]) => {
        // Get latest positions for each product
        const productRankings = rankings.map(r => {
          const latestPosition = r.positions
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .find(p => utils.isValidPosition(p));

          return {
            productName: r.productName || '',
            position: latestPosition?.position || 0,
            asin: latestPosition?.asin || '',
            market: latestPosition?.market || r.market,
            ctr_score: r.ctr_score,
            growth_opportunity: r.growth_opportunity,
            main_niche_score: r.main_niche_score,
            ctr: r.ctr,
            change_significance: r.change_significance
          };
        });

        // Create rankings for each week
        const weeklyRankings = sortedDates.map(date => {
          const rankingsForDate = rankings
            .map(ranking => {
              const position = ranking.positions.find(p => 
                utils.getWeekStartDate(p.date) === date && 
                utils.isValidPosition(p)
              );
              
              if (!position) return null;
              
              return {
                productName: ranking.productName || '',
                position: position.position,
                asin: position.asin,
                market: position.market,
                ctr_score: position.ctr_score,
                growth_opportunity: position.growth_opportunity,
                main_niche_score: position.main_niche_score,
                ctr: position.ctr,
                change_significance: position.change_significance
              };
            })
            .filter((r): r is NonNullable<typeof r> => r !== null);

          if (rankingsForDate.length === 0) return null;

          return {
            date,
            products: rankingsForDate,
            asinChanges: []
          };
        }).filter((r): r is NonNullable<typeof r> => r !== null);

        return {
          keyword,
          productRankings,
          rankings: weeklyRankings,
          asinChanges: [],
          searchVolume: rankings[0].search_volume
        };
      });
    }
  };

  // Event Handlers
  const handlers: RankingsViewHandlers = {
    handleClientChange: (
      _event: SyntheticEvent<Element, Event>,
      newValue: Client | { id: number, name: string, products: [] } | null,
      _reason: AutocompleteChangeReason,
      _details?: AutocompleteChangeDetails<Client | { id: number, name: string, products: [] }>
    ) => {
      lastChangeRef.current = Date.now();
      setIsPending(true);
      setClientDropdownOpen(false); // Close dropdown immediately after selection
      setState(prev => ({
        ...prev,
        selectedClient: newValue,
        selectedProducts: [],
        customAsins: newValue?.id === -1 ? prev.customAsins : ''
      }));
      setFilters((prev: RankingFilter) => ({
        ...prev,
        client_id: newValue?.id || 0,
        product_ids: '',
        custom_asins: newValue?.id === -1 ? prev.custom_asins : ''
      }));
    },

    handleProductChange: (
      _event: SyntheticEvent<Element, Event>,
      newValue: Product[],
      _reason: AutocompleteChangeReason,
      _details?: AutocompleteChangeDetails<Product>
    ) => {
      lastChangeRef.current = Date.now();
      setIsPending(true);
      
      const selectAll = newValue.some(p => p.id === -1);
      let selectedProducts: Product[] = [];
      
      if (selectAll) {
        // If "Select All" is chosen, select all products except the "Select All" option
        selectedProducts = state.selectedClient?.products.filter(p => p.id !== -1) || [];
      } else {
        // Otherwise, use the selected products
        selectedProducts = newValue;
      }
      
      setState(prev => ({
        ...prev,
        selectedProducts
      }));
      
      setFilters(prev => ({
        ...prev,
        product_ids: selectedProducts.map(p => p.id.toString()).join(',')
      }));
    },
  };

  const handleMarketChange = (event: SelectChangeEvent<number>) => {
    const raw = Number(event.target.value);
    const value = raw === 0 ? undefined : raw;
    setFilters((prev: RankingFilter) => ({
      ...prev,
      market: value
    }));
  };

  // Effects
  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await api.getClients();
        // Sort clients by the number of products they have in descending order
        const sortedClients = [...response].sort((a, b) => {
          return (b.products?.length || 0) - (a.products?.length || 0);
        });
        setState(prev => ({
          ...prev,
          clients: sortedClients
        }));
      } catch (err) {
        setState(prev => ({
          ...prev,
          error: 'Failed to load clients'
        }));
      }
    };

    loadClients();
  }, []);

  // Parse URL on initial load (after clients are available)
  useEffect(() => {
    if (hasAppliedUrlRef.current) return;
    if (state.clients.length === 0) return;

    // If no meaningful params, skip
    const hasAny = ['clientId', 'productIds', 'asins', 'days', 'minSV', 'maxPos', 'market']
      .some((k) => searchParams.has(k));
    if (!hasAny) {
      hasAppliedUrlRef.current = true;
      return;
    }

    const num = (v: string | null) => (v == null ? NaN : Number(v));
    const clampToSet = (val: number, allowed: number[], fallback: number) =>
      allowed.includes(val) ? val : fallback;

    const clientIdRaw = searchParams.get('clientId');
    const productIdsRaw = searchParams.get('productIds');
    const asinsRaw = searchParams.get('asins');
    const daysRaw = searchParams.get('days');
    const minSVRaw = searchParams.get('minSV');
    const maxPosRaw = searchParams.get('maxPos');
    const marketRaw = searchParams.get('market');

    let selectedClient: Client | { id: number; name: string; products: [] } | null = null;
    let selectedProducts: Product[] = [];

    const parsedClientId = num(clientIdRaw);
    if (!isNaN(parsedClientId)) {
      if (parsedClientId === -1) {
        selectedClient = { id: -1, name: 'No Client', products: [] } as any;
      } else {
        selectedClient = state.clients.find((c) => c.id === parsedClientId) || null;
      }
    }

    if (selectedClient && selectedClient.id !== -1 && productIdsRaw) {
      const clientProducts = (selectedClient as Client).products || [];
      if (productIdsRaw === 'all') {
        selectedProducts = clientProducts.filter((p) => p.id !== -1);
      } else {
        const idSet = new Set(
          productIdsRaw
            .split(',')
            .map((s) => Number(s.trim()))
            .filter((n) => !isNaN(n))
        );
        selectedProducts = clientProducts.filter((p) => idSet.has(p.id));
      }
    }

    const nextDays = clampToSet(Number(daysRaw), [30, 60, 90, 120], 90);
    const nextMinSV = clampToSet(Number(minSVRaw), [0, 100, 1000, 10000, 100000], 100);
    const nextMaxPos = clampToSet(Number(maxPosRaw), [10, 25, 50, 100], 100);
    const nextMarketVal = Number(marketRaw);
    const nextMarket = isNaN(nextMarketVal) ? 1 : nextMarketVal === 0 ? undefined : nextMarketVal;

    // Apply state updates in a batch
    setState((prev) => ({
      ...prev,
      selectedClient: selectedClient ?? prev.selectedClient,
      selectedProducts: selectedProducts.length ? selectedProducts : prev.selectedProducts,
      customAsins: selectedClient?.id === -1 ? (asinsRaw || prev.customAsins || '') : '',
    }));

    setFilters((prev) => ({
      ...prev,
      client_id: selectedClient ? selectedClient.id : prev.client_id,
      product_ids:
        selectedClient && selectedClient.id !== -1
          ? selectedProducts.map((p) => p.id.toString()).join(',')
          : '',
      custom_asins: selectedClient?.id === -1 ? (asinsRaw || '') : '',
      days: isNaN(nextDays) ? prev.days : nextDays,
      min_search_volume: isNaN(nextMinSV) ? prev.min_search_volume : nextMinSV,
      max_position: isNaN(nextMaxPos) ? prev.max_position : nextMaxPos,
      market: nextMarket,
    }));

    hasAppliedUrlRef.current = true;
  }, [state.clients, searchParams]);

  // Keep URL in sync with current selection and filters
  useEffect(() => {
    // Prevent initial URL overwrite until initial URL params are applied
    if (!hasAppliedUrlRef.current) return;
    // Build canonical params ordering
    const params = new URLSearchParams();

    if (state.selectedClient) {
      params.set('clientId', String(state.selectedClient.id));

      if (state.selectedClient.id === -1) {
        const asins = (state.customAsins || '').trim();
        if (asins) params.set('asins', asins);
      } else {
        const total = state.selectedClient.products?.filter((p) => p.id !== -1).length || 0;
        const selectedCount = state.selectedProducts.length;
        if (selectedCount > 0) {
          const allSelected = total > 0 && selectedCount === total;
          if (allSelected) params.set('productIds', 'all');
          else params.set('productIds', state.selectedProducts.map((p) => p.id).join(','));
        }
      }
    }

    params.set('days', String(filters.days));
    params.set('minSV', String(filters.min_search_volume));
    params.set('maxPos', String(filters.max_position));
    params.set('market', String(filters.market ?? 0));

    const next = params.toString();
    const curr = searchParams.toString();
    if (next !== curr) {
      setSearchParams(params, { replace: true });
    }
  }, [state.selectedClient, state.selectedProducts, state.customAsins, filters.days, filters.min_search_volume, filters.max_position, filters.market, setSearchParams]);

  useEffect(() => {
    const loadRankings = async () => {
      // For "No Client" option, check if we have custom ASINs
      if (filters.client_id === -1) {
        if (!filters.custom_asins || isPending) return;
      } else {
        // For regular client, check if we have selected products
        if (!filters.client_id || !filters.product_ids || isPending) return;
      }

      setState(prev => ({ ...prev, isLoading: true, error: '' }));

      try {
        const requestParams = {
          client_id: state.selectedClient?.id || 0,
          product_ids: state.selectedClient?.id === -1 ? ' ' : state.selectedProducts.map(p => p.id.toString()).join(','),
          days: filters.days,
          min_search_volume: filters.min_search_volume,
          max_position: filters.max_position,
          market: filters.market,
          keywords: [],
          custom_asins: state.selectedClient?.id === -1 ? state.customAsins : undefined
        };

        // Only fetch products if we have a regular client
        let productsResponse: Product[] = [];
        if (state.selectedClient?.id !== -1 && state.selectedClient) {
          productsResponse = await api.getProducts(state.selectedClient.id);
        }

        const response = await api.getRankings(requestParams);

        if (state.selectedClient?.id !== -1) {
          setProducts(productsResponse);
        }

        const rankingsWithProducts = response.map(ranking => 
          utils.processRanking(ranking, state.selectedClient?.id === -1 ? [] : productsResponse)
        );

        const organized = utils.organizeRankingsByKeyword(rankingsWithProducts);
        setUnfilteredRankings(organized);
        setState(prev => ({
          ...prev,
          organizedRankings: organized,
          isLoading: false
        }));
      } catch (err) {
        console.error('Failed to load rankings:', err);
        setState(prev => ({
          ...prev,
          error: 'Failed to load rankings. Please try again.',
          isLoading: false
        }));
        setUnfilteredRankings([]);
      }
    };

    loadRankings();
  }, [state.selectedClient, state.selectedProducts, state.customAsins, filters, isPending]);

  useEffect(() => {
    if (!filters.client_id || !filters.product_ids && !filters.custom_asins) return;
    
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
    }

    setIsPending(true);
    pendingTimeoutRef.current = setTimeout(() => {
      applyPendingChanges();
    }, 500);

    return () => {
      if (pendingTimeoutRef.current) {
        clearTimeout(pendingTimeoutRef.current);
      }
    };
  }, [filters]);

  // Keep state.organizedRankings as the unfiltered API-organized data.

  // Effect to load keyword tags when a product is selected - Performance optimized
  useEffect(() => {
    // Use a ref to track if the component is still mounted
    const isMounted = { current: true };
    
    const loadKeywordTags = async () => {
      if (!state.selectedProducts || state.selectedProducts.length === 0) {
        setKeywordTags([]);
        return;
      }

      try {
        // Get tags for the first selected product
        const productId = state.selectedProducts[0].id;
        
        // Add a small delay to prevent excessive API calls during rapid selections
        const tags = await api.getProductKeywordTags(productId);
        
        // Only update state if component is still mounted
        if (isMounted.current) {
          // Pre-process tags to include keyword_count
          const processedTags = tags.map(tag => ({
            ...tag,
            keyword_count: tag.keywords?.length || 0
          }));
          
          setKeywordTags(processedTags);
        }
      } catch (err) {
        console.error('Failed to load keyword tags:', err);
      }
    };

    loadKeywordTags();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted.current = false;
    };
  }, [state.selectedProducts]);

  // removed duplicate fetch effect to rely on main loadRankings effect

  // Utility function to calculate median of an array of numbers
  const calculateMedian = (numbers: number[]): number => {
    if (!numbers.length) return 0;
    
    const sorted = [...numbers].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    
    if (sorted.length % 2 === 0) {
      return (sorted[middle - 1] + sorted[middle]) / 2;
    }
    
    return sorted[middle];
  };

  // Handle Copy Link action
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopySnackbarOpen(true);
    } catch (e) {
      // Fallback
      try {
        const el = document.createElement('textarea');
        el.value = window.location.href;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        setCopySnackbarOpen(true);
      } catch {
        console.error('Failed to copy link');
      }
    }
  };

  // Function to handle CSV export
  const handleExportCsv = () => {
    if (!filteredAndSortedRankings.length) return;
    
    setIsExporting(true);
    
    try {
      // Prepare CSV header
      const headers = [
        'Keyword',
        'CTR Score',
        'Growth Opportunity',
        'Main Niche Score',
        'Search Volume',
        'Last Rank',
        'Median Rank',
        'Last Rank ASIN',
        'Last Rank Product',
        'Market'
      ];
      
      // Prepare CSV rows
      const rows = filteredAndSortedRankings.map(group => {
        // Get the most recent ranking data
        const lastRanking = group.rankings[0];
        const lastProduct = lastRanking?.products[0];
        
        // Calculate median values
        const allPositions: number[] = [];
        
        // Collect all positions for median calculation
        group.rankings.forEach(ranking => {
          if (!ranking) return;
          ranking.products.forEach(product => {
            if (product.position !== undefined) allPositions.push(product.position);
          });
        });
        
        // Get current values for the keyword
        const currentCtrScore = lastProduct?.ctr_score?.toFixed(2) || '';
        const currentGrowthOpp = lastProduct?.growth_opportunity?.toFixed(2) || '';
        
        // Find the maximum main_niche_score across all rankings and products
        const allNicheScores = group.rankings.flatMap(r => 
          r?.products ? r.products.map(p => p?.main_niche_score || 0) : []
        ).filter((item): item is NonNullable<typeof item> => item !== null);
        const maxNicheScore = allNicheScores.length > 0 ? Math.max(...allNicheScores) : 0;
        
        return [
          `"${group.keyword.replace(/"/g, '""')}"`, // Escape quotes in keyword
          currentCtrScore,
          currentGrowthOpp,
          maxNicheScore.toFixed(2),
          group.searchVolume,
          lastProduct?.position || '',
          calculateMedian(allPositions).toFixed(2),
          lastProduct?.asin || '',
          lastProduct?.productName ? `"${lastProduct.productName.replace(/"/g, '""')}"` : '',
          lastProduct?.market ? getCountryFlag(lastProduct.market) : ''
        ];
      });
      
      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `keyword_rankings_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error exporting to CSV:', error);
      // You might want to show an error toast/notification here
    } finally {
      setIsExporting(false);
    }
  };

  // Render Functions
  const renderWeeklyPosition = (week: RankingData, prevWeek: RankingData | null) => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {week.products.map((product) => {
          // Look for any product in the previous week, not just ones with matching ASINs
          const prevProduct = prevWeek?.products?.find(p => p.productName === product.productName);
          const change = prevProduct ? utils.getPositionChange(product.position, prevProduct.position) : '';
          // Check if the ASIN changed for this product
          const asinChanged = prevProduct && prevProduct.asin !== product.asin;
          const prevAsin = prevProduct?.asin;

          // Calculate CTR Score change
          const ctrChange = (() => {
            // Only calculate if both current and previous products exist and have ctr_score
            if (!product?.ctr_score || !prevProduct?.ctr_score) return null;
            
            // Ensure we have valid numbers
            const currentCtr = Number(product.ctr_score);
            const prevCtr = Number(prevProduct.ctr_score);
            
            if (isNaN(currentCtr) || isNaN(prevCtr)) return null;
            
            const value = Math.round(currentCtr - prevCtr);
            const percentage = prevCtr !== 0 ? ((currentCtr - prevCtr) / Math.abs(prevCtr) * 100).toFixed(1) : '0.0';
            
            return { value, percentage };
          })();

          // Calculate growth opportunity change
          const growthChange = (() => {
            // Only calculate if both current and previous products exist and have growth_opportunity
            if (!product?.growth_opportunity || !prevProduct?.growth_opportunity) return null;
            
            // Ensure we have valid numbers
            const currentGrowth = Number(product.growth_opportunity);
            const prevGrowth = Number(prevProduct.growth_opportunity);
            
            if (isNaN(currentGrowth) || isNaN(prevGrowth)) return null;
            
            const value = Math.round(currentGrowth - prevGrowth);
            const percentage = prevGrowth !== 0 ? ((currentGrowth - prevGrowth) / Math.abs(prevGrowth) * 100).toFixed(1) : '0.0';
            
            return { value, percentage };
          })();

          // Format tooltip content
          const tooltipContent = (
            <Box sx={{ 
              py: 0.5, 
              px: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              minWidth: '150px'
            }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', mb: 0.5 }}>
                {week.date}
              </Typography>
              {ctrChange && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    CTR Score:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: Number(ctrChange.value) > 0 ? 'success.main' : 'error.main',
                      fontWeight: 'medium'
                    }}
                  >
                    {Number(ctrChange.value) > 0 ? '+' : ''}{ctrChange.value} ({ctrChange.percentage}%)
                  </Typography>
                </Box>
              )}
              {growthChange && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Growth Opportunity:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: Number(growthChange.value) > 0 ? 'success.main' : 'error.main',
                      fontWeight: 'medium'
                    }}
                  >
                    {Number(growthChange.value) > 0 ? '+' : ''}{growthChange.value} ({growthChange.percentage}%)
                  </Typography>
                </Box>
              )}
            </Box>
          );

          return (
            <Box key={`${product.productName}-${product.asin}`} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Tooltip 
                title={tooltipContent}
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: 'background.paper',
                      boxShadow: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      color: 'text.primary',
                      '& .MuiTooltip-arrow': {
                        color: 'background.paper',
                        '&::before': {
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper'
                        }
                      }
                    }
                  }
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  cursor: 'help'
                }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 'bold',
                      color: utils.getPositionColor(product.position)
                    }}
                  >
                    {product.position}
                  </Typography>
                  {change && (
                    <Typography
                      variant="caption"
                      sx={{
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 0.5,
                        bgcolor: change.includes('↑') ? 'success.light' : 
                                change.includes('↓') ? 'error.light' : 
                                'action.hover',
                        color: change.includes('↑') ? 'success.dark' : 
                                change.includes('↓') ? 'error.dark' : 
                                'text.secondary',
                        fontWeight: 'medium'
                      }}
                    >
                      {change}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
              {asinChanged && (
                <Tooltip 
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="caption">ASIN Change:</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{prevAsin}</Typography>
                        <span>→</span>
                        <Typography variant="caption">{product.asin}</Typography>
                      </Box>
                    </Box>
                  }
                  arrow
                >
                  <SwapHorizIcon 
                    sx={{ 
                      fontSize: '16px', 
                      color: 'warning.main',
                      ml: 1,
                      verticalAlign: 'middle',
                      cursor: 'help'
                    }} 
                  />
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderKeywordInfo = (group: OrganizedRankings) => {
    const firstProduct = group.rankings[0]?.products[0];
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          <a
            href={`https://${getAmazonDomain(filters.market || 0)}/s?k=${encodeURIComponent(group.keyword)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}
          >
            {group.keyword}
            <LaunchIcon sx={{ fontSize: '1rem', ml: 0.5, color: 'text.secondary' }} />
          </a>
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Search Volume:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {group.searchVolume.toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              CTR Score:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {firstProduct?.ctr_score?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '-'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Growth Opportunity:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {firstProduct?.growth_opportunity?.toLocaleString(undefined, {maximumFractionDigits: 0}) || '-'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Main Niche Score:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {(() => {
                // Find the maximum main_niche_score across all rankings and products
                const allScores = group.rankings.flatMap(r => r?.products ? r.products.map(p => p?.main_niche_score || 0) : []).filter((item): item is NonNullable<typeof item> => item !== null);
                const maxScore = allScores.length > 0 ? Math.max(...allScores) : 0;
                return maxScore.toLocaleString(undefined, {maximumFractionDigits: 0});
              })() || '-'}
            </Typography>
          </Box>
        </Box>
        {group.productRankings.map(pr => (
          <Box key={`${pr.productName}-${pr.asin}`} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" sx={{ color: 'primary.main' }}>
              {pr.productName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <a
                href={getAmazonProductUrl(pr.asin, pr.market)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: 'inherit',
                  textDecoration: 'none'
                }}
              >
                <Typography variant="body2" component="span" color="text.secondary">
                  {pr.asin}
                </Typography>
                <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.7, color: 'text.secondary' }} />
              </a>
              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                {getCountryFlag(pr.market)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Client and Product selectors */}
      <FormControl fullWidth sx={{ mb: 1.5 }}>
        <Autocomplete<Client | { id: number, name: string, products: [] }, false, false, false>
          size="small"
          open={clientDropdownOpen}
          onOpen={() => setClientDropdownOpen(true)}
          onClose={() => setClientDropdownOpen(false)}
          value={state.selectedClient}
          onChange={handlers.handleClientChange}
          options={[
            { id: -1, name: "No Client", products: [] },
            ...state.clients
          ]}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField {...params} label="Client" />
          )}
        />
      </FormControl>

      {state.selectedClient && state.selectedClient.id === -1 ? (
        <FormControl fullWidth sx={{ mb: 1.5 }}>
          <TextField
            label="ASINs (comma separated)"
            size="small"
            placeholder="Enter one or more ASINs separated by commas"
            value={state.customAsins || ''}
            onChange={(e) => {
              setState(prev => ({
                ...prev,
                customAsins: e.target.value
              }));
              setFilters(prev => ({
                ...prev,
                custom_asins: e.target.value
              }));
            }}
          />
        </FormControl>
      ) : state.selectedClient && (
        <FormControl fullWidth sx={{ mb: 1.5 }}>
          <Autocomplete<Product, true, false, false>
            multiple
            size="small"
            open={productsDropdownOpen}
            onOpen={() => setProductsDropdownOpen(true)}
            onClose={(event, reason) => {
              // Keep open if closed by selecting an item
              if (reason === 'selectOption') return;
              setProductsDropdownOpen(false);
            }}
            value={state.selectedClient.products.filter(product => 
              state.selectedProducts.some(p => p.id === product.id)
            )}
            onChange={handlers.handleProductChange}
            options={[...state.selectedClient.products].sort((a, b) => (b.asins?.length || 0) - (a.asins?.length || 0))}
            getOptionLabel={(option) => option.id === -1 ? 'Select All' : option.name}
            renderOption={(props, option, { selected }) => {
              if (option.id === -1) {
                const { key, ...otherProps } = props;
                return (
                  <MenuItem
                    key={key}
                    {...otherProps}
                    divider
                    sx={{
                      bgcolor: 'background.paper',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <Checkbox 
                      checked={state.selectedClient?.products && 
                        state.selectedProducts.length === state.selectedClient.products.length} 
                      indeterminate={state.selectedClient?.products && 
                        state.selectedProducts.length > 0 && 
                        state.selectedProducts.length < state.selectedClient.products.length}
                    />
                    <ListItemText primary="Select All" />
                  </MenuItem>
                );
              }
              const { key, ...otherProps } = props;
              return (
                <MenuItem key={key} {...otherProps}>
                  <Checkbox checked={selected} />
                  <ListItemText primary={option.name} />
                </MenuItem>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Products" />
            )}
            filterOptions={(options, params) => {
              const filtered = options.filter(option => 
                option.name.toLowerCase().includes(params.inputValue.toLowerCase())
              );
              return [{ id: -1, name: 'Select All', client_id: -1, created_at: '', asins: [] } as Product, ...filtered];
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disableCloseOnSelect={true}
          />
        </FormControl>
      )}

      {/* Filters */}
      <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Days
            </Typography>
            <Select
              size="small"
              value={filters.days}
              onChange={(e) => setFilters((prev: RankingFilter) => ({ ...prev, days: Number(e.target.value) }))}
            >
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={60}>60 days</MenuItem>
              <MenuItem value={90}>90 days</MenuItem>
              <MenuItem value={120}>120 days</MenuItem>
            </Select>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Min Search Volume
            </Typography>
            <Select
              size="small"
              value={filters.min_search_volume}
              onChange={(e) => setFilters((prev: RankingFilter) => ({ ...prev, min_search_volume: Number(e.target.value) }))}
            >
              <MenuItem value={0}>No minimum</MenuItem>
              <MenuItem value={100}>100+</MenuItem>
              <MenuItem value={1000}>1,000+</MenuItem>
              <MenuItem value={10000}>10,000+</MenuItem>
              <MenuItem value={100000}>100,000+</MenuItem>
            </Select>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Max Position
            </Typography>
            <Select
              size="small"
              value={filters.max_position}
              onChange={(e) => setFilters((prev: RankingFilter) => ({ ...prev, max_position: Number(e.target.value) }))}
            >
              <MenuItem value={10}>Top 10</MenuItem>
              <MenuItem value={25}>Top 25</MenuItem>
              <MenuItem value={50}>Top 50</MenuItem>
              <MenuItem value={100}>Top 100</MenuItem>
            </Select>
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Market
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={filters.market ?? 0}
                onChange={handleMarketChange}
                size="small"
              >
                <MenuItem value={0}>{getMarketLabel(0)}</MenuItem>
                <MenuItem value={Market.US}>{getMarketLabel(Market.US)}</MenuItem>
                <MenuItem value={Market.UK}>{getMarketLabel(Market.UK)}</MenuItem>
                <MenuItem value={Market.DE}>{getMarketLabel(Market.DE)}</MenuItem>
                <MenuItem value={Market.FR}>{getMarketLabel(Market.FR)}</MenuItem>
                <MenuItem value={Market.IT}>{getMarketLabel(Market.IT)}</MenuItem>
                <MenuItem value={Market.IN}>{getMarketLabel(Market.IN)}</MenuItem>
                <MenuItem value={Market.ES}>{getMarketLabel(Market.ES)}</MenuItem>
                <MenuItem value={Market.CA}>{getMarketLabel(Market.CA)}</MenuItem>
                <MenuItem value={Market.MX}>{getMarketLabel(Market.MX)}</MenuItem>
                <MenuItem value={Market.AU}>{getMarketLabel(Market.AU)}</MenuItem>
                <MenuItem value={Market.NL}>{getMarketLabel(Market.NL)}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Advanced Filters Section - Improved Alignment */}
        {unfilteredRankings.length > 0 && (
          <Box sx={{ 
            mt: 2,
            borderRadius: 1,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider'
          }}>
            {/* Filter Bar */}
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              p: 1.5, 
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              alignItems: 'center'
            }}>
              {/* Keyword Filter */}
              <Autocomplete<Keyword, true, false, true>
                multiple
                size="small"
                freeSolo
                disablePortal
                disableCloseOnSelect
                limitTags={3}
                open={keywordsDropdownOpen}
                onOpen={() => setKeywordsDropdownOpen(true)}
                onClose={(event, reason) => {
                  // Keep open if closed by selecting an item
                  if (reason === 'selectOption') {
                    return;
                  }
                  setKeywordsDropdownOpen(false);
                }}
                sx={{ 
                  flex: '1 1 300px', 
                  minWidth: 200,
                  '& .MuiInputBase-root': {
                    height: 40
                  }
                }}
                value={keywords.filter(k => filters.keywords.includes(k.keyword))}
                onChange={(_, newValue) => {
                  // Handle "Select All" option
                  if (newValue.some(k => k && typeof k !== 'string' && k.id === -1)) {
                    const filteredKeywords = keywords
                      .filter(k => k.keyword.toLowerCase().includes((inputValue || '').toLowerCase()))
                      .map(k => k.keyword);
                      
                    // If all filtered keywords are selected, deselect all
                    if (filteredKeywords.every(k => filters.keywords.includes(k))) {
                      setFilters((prev: RankingFilter) => ({ ...prev, keywords: [] }));
                    } else {
                      // Otherwise select all filtered keywords
                      setFilters((prev: RankingFilter) => ({ ...prev, keywords: filteredKeywords }));
                    }
                    return;
                  }
                  setFilters((prev: RankingFilter) => ({
                    ...prev,
                    keywords: newValue
                      .filter((v): v is Keyword => typeof v !== 'string')
                      .map(k => k.keyword)
                  }));
                }}
                inputValue={inputValue}
                onInputChange={(_, value) => {
                  setInputValue(value);
                }}
                filterOptions={(options, params) => {
                  const filtered = options.filter(option => 
                    option.id === -1 || // Always include Select All
                    option.keyword.toLowerCase().includes(params.inputValue.toLowerCase())
                  );
                  return filtered;
                }}
                options={[
                  { 
                    id: -1, 
                    keyword: 'Select All',
                    client_id: -1,
                    created_at: new Date().toISOString(),
                    search_volume: 0
                  },
                  ...keywords
                ]}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  return option.id === -1 ? 'Select All' : option.keyword;
                }}
                renderOption={(props, option, { selected }) => {
                  if (typeof option === 'string') return null;
                  return (
                    <li {...props}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlank fontSize="small" />}
                        checkedIcon={<CheckBox fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={
                          option.id === -1 
                            ? keywords
                                .filter(k => k.keyword.toLowerCase().includes((inputValue || '').toLowerCase()))
                                .every(k => filters.keywords.includes(k.keyword))
                            : selected
                        }
                        />
                        {option.id === -1 ? 'Select All' : option.keyword}
                      </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter Keywords"
                    placeholder="Filter keywords..."
                  />
                )}
              />
              
              {/* Tag Filter Button */}
              {keywordTags.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    <FilterList fontSize="small" />
                  }
                  onClick={(e) => {
                    setTagMenuAnchorEl(e.currentTarget);
                  }}
                  sx={{
                    height: 40,
                    minWidth: 'auto',
                    textTransform: 'none',
                    borderColor: 'divider',
                    px: 1.5,
                    whiteSpace: 'nowrap',
                    ml: 1, // Add left margin to create space between filter and tags
                    '&:hover': { borderColor: 'primary.main' }
                  }}
                >
                  Tags
                </Button>
              )}
              
              {/* Tag Menu - Performance Optimized */}
              <Menu
                anchorEl={tagMenuAnchorEl}
                open={Boolean(tagMenuAnchorEl)}
                onClose={() => setTagMenuAnchorEl(null)}
                PaperProps={{
                  sx: { maxWidth: 250, maxHeight: 300 }
                }}
                MenuListProps={{
                  dense: true,
                  sx: { py: 0.5 }
                }}
                disableAutoFocusItem
                transitionDuration={150}
              >
                {keywordTags.map((tag) => {
                  // Pre-calculate the available keywords for this tag
                  const tagKeywords = tag.keywords.map(k => k.keyword);
                  const availableKeywordsCount = tagKeywords.filter(keyword => 
                    keywords.some(k => k.keyword === keyword)
                  ).length;
                  
                  return (
                    <MenuItem 
                      key={tag.id} 
                      dense
                      disabled={availableKeywordsCount === 0}
                      onClick={() => {
                        if (availableKeywordsCount > 0) {
                          // Only do this calculation when clicked
                          const tagKeywords = tag.keywords.map(k => k.keyword);
                          const availableTagKeywords = tagKeywords.filter(keyword => 
                            keywords.some(k => k.keyword === keyword)
                          );
                          
                          setFilters((prev: RankingFilter) => ({
                            ...prev,
                            keywords: [...new Set([...prev.keywords, ...availableTagKeywords])]
                          }));
                        }
                        setTagMenuAnchorEl(null);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box 
                          component="span" 
                          sx={{ 
                            display: 'inline-block', 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            bgcolor: tag.color || '#ccc',
                            mr: 1,
                            flexShrink: 0
                          }} 
                        />
                        <Typography 
                          variant="body2" 
                          noWrap 
                          sx={{ 
                            flex: 1,
                            color: availableKeywordsCount === 0 ? 'text.disabled' : 'inherit'
                          }}
                        >
                          {tag.name} ({availableKeywordsCount})
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Menu>
              
              {/* Sort Controls */}
              <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={handleCopyLink}
                  sx={{ height: 40, whiteSpace: 'nowrap' }}
                >
                  Copy link
                </Button>
                <FormControl size="small" sx={{ width: 150 }}>
                  <InputLabel>Sort By</InputLabel>
                  <Select
                    value={sortConfig.key}
                    label="Sort By"
                    sx={{ height: 40 }}
                    onChange={(e) => setSortConfig(current => ({
                      key: e.target.value as typeof sortConfig.key,
                      direction: current.key === e.target.value && current.direction === 'desc' ? 'asc' : 'desc'
                    }))}
                  >
                    <MenuItem value="searchVolume">Search Volume</MenuItem>
                    <MenuItem value="lastPosition">Position</MenuItem>
                    <MenuItem value="keyword">Keyword</MenuItem>
                    <MenuItem value="ctrScore">CTR Score</MenuItem>
                    <MenuItem value="growth_opportunity">Growth Opportunity</MenuItem>
                    <MenuItem value="main_niche_score">Main Niche Score</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ width: 150 }}>
                  <InputLabel>Sort Direction</InputLabel>
                  <Select
                    value={sortConfig.direction}
                    label="Sort Direction"
                    sx={{ height: 40 }}
                    onChange={(e) => setSortConfig(prev => ({
                      ...prev,
                      direction: e.target.value as 'asc' | 'desc'
                    }))}
                  >
                    <MenuItem value="desc">Descending</MenuItem>
                    <MenuItem value="asc">Ascending</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Advanced Filters Toggle */}
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                px: 1.5,
                py: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                bgcolor: advancedFiltersExpanded ? 'action.hover' : 'background.paper',
                '&:hover': { bgcolor: 'action.hover' }
              }}
              onClick={() => setAdvancedFiltersExpanded(!advancedFiltersExpanded)}
            >
              <Typography variant="body2" color="text.secondary" sx={{ 
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
              }}>
                Advanced Filters
              </Typography>
              <Box sx={{ flexGrow: 1 }} />
              {advancedFiltersExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </Box>

            {/* Collapsible Range Filters */}
            <Collapse in={advancedFiltersExpanded}>
              <Box sx={{ 
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                p: 1.5,
                bgcolor: 'action.hover'
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '180px', flex: '1 0 auto' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Position Range
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Min"
                      type="number"
                      value={inputState.position.min}
                      onChange={(e) => handleInputChange('position', 'min', e.target.value)}
                      sx={{ width: '90px' }}
                    />
                    <TextField
                      size="small"
                      placeholder="Max"
                      type="number"
                      value={inputState.position.max}
                      onChange={(e) => handleInputChange('position', 'max', e.target.value)}
                      sx={{ width: '90px' }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '180px', flex: '1 0 auto' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    CTR Score Range
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Min"
                      type="number"
                      value={inputState.ctrScore.min}
                      onChange={(e) => handleInputChange('ctrScore', 'min', e.target.value)}
                      sx={{ width: '90px' }}
                    />
                    <TextField
                      size="small"
                      placeholder="Max"
                      type="number"
                      value={inputState.ctrScore.max}
                      onChange={(e) => handleInputChange('ctrScore', 'max', e.target.value)}
                      sx={{ width: '90px' }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '180px', flex: '1 0 auto' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Growth Opportunity Range
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Min"
                      type="number"
                      value={inputState.growth_opportunity.min}
                      onChange={(e) => handleInputChange('growth_opportunity', 'min', e.target.value)}
                      sx={{ width: '90px' }}
                    />
                    <TextField
                      size="small"
                      placeholder="Max"
                      type="number"
                      value={inputState.growth_opportunity.max}
                      onChange={(e) => handleInputChange('growth_opportunity', 'max', e.target.value)}
                      sx={{ width: '90px' }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '180px', flex: '1 0 auto' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Main Niche Score Range
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Min"
                      type="number"
                      value={inputState.main_niche_score.min}
                      onChange={(e) => handleInputChange('main_niche_score', 'min', e.target.value)}
                      sx={{ width: '90px' }}
                    />
                    <TextField
                      size="small"
                      placeholder="Max"
                      type="number"
                      value={inputState.main_niche_score.max}
                      onChange={(e) => handleInputChange('main_niche_score', 'max', e.target.value)}
                      sx={{ width: '90px' }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: '180px', flex: '1 0 auto' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Search Volume Range
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Min"
                      type="number"
                      value={inputState.searchVolume.min}
                      onChange={(e) => handleInputChange('searchVolume', 'min', e.target.value)}
                      sx={{ width: '90px' }}
                    />
                    <TextField
                      size="small"
                      placeholder="Max"
                      type="number"
                      value={inputState.searchVolume.max}
                      onChange={(e) => handleInputChange('searchVolume', 'max', e.target.value)}
                      sx={{ width: '90px' }}
                    />
                  </Box>
                </Box>
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>

      {/* Add Period Performance Summary */}
      {filteredAndSortedRankings.length > 0 && (
        <PeriodPerformanceSummary 
          rankings={filteredAndSortedRankings} 
          loading={state.isLoading}
        />
      )}
      
      {/* Rankings Table */}
      {state.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : state.error ? (
          <Typography color="error">{state.error}</Typography>
        ) : filteredAndSortedRankings.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'background.default', position: 'relative' }}>
                  <TableCell sx={{ minWidth: 300 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={isExporting ? 'Exporting...' : 'Export to CSV'}>
                        <IconButton
                          onClick={handleExportCsv}
                          disabled={isExporting || !filteredAndSortedRankings.length}
                          size="small"
                          sx={{ p: 0.5, position: 'absolute', left: 8, top: '4px' }}
                        >
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Typography variant="subtitle2" sx={{ ml: 4 }}>
                        Keyword
                      </Typography>
                    </Box>
                  </TableCell>
                  {/* Get all unique dates from unfiltered rankings */}
                  {Array.from(new Set(
                    unfilteredRankings.flatMap(ranking => 
                      ranking.rankings
                        .filter((r): r is NonNullable<typeof r> => r !== null)
                        .map(r => r.date)
                    )
                  ))
                  .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
                  .map(date => {
                    const startDate = new Date(date);
                    const endDate = new Date(startDate);
                    endDate.setDate(startDate.getDate() + 6);
                    
                    return (
                      <TableCell key={date} align="center">
                        <Typography variant="subtitle2">
                          {startDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                          {' - '}
                          {endDate.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedRankings.map((group) => {
                  // Use the same dates from unfiltered rankings
                  const allDates = Array.from(new Set(
                    unfilteredRankings.flatMap(ranking => 
                      ranking.rankings
                        .filter((r): r is NonNullable<typeof r> => r !== null)
                        .map(r => r.date)
                    )
                  )).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

                  return (
                    <TableRow 
                      key={group.keyword}
                      hover
                    >
                      <TableCell sx={{ borderBottom: '1px solid #eee' }}>
                        {renderKeywordInfo(group)}
                      </TableCell>
                      {allDates.map((date, idx) => {
                        const week = group.rankings.find(r => r && r.date === date) || null;
                        const prevWeek = idx < allDates.length - 1 
                          ? group.rankings.find(r => r && r.date === allDates[idx + 1]) || null
                          : null;
                        
                        return (
                          <TableCell key={date} align="center">
                            {week ? renderWeeklyPosition(week, prevWeek) : (
                              <Typography
                                variant="body1"
                                sx={{
                                  color: 'text.secondary',
                                  fontWeight: 'medium'
                                }}
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography>No rankings found matching the current filters.</Typography>
        )}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default RankingsView;
